// Language context for multi-language support
import { createContext, useContext, useState, useEffect, ReactNode } from "react";
import { theme } from "@/config/portfolio.config";

type Language = "fr" | "en";

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string) => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

// Translations
const translations: Record<Language, Record<string, string>> = {
  fr: {
    // Navigation
    "nav.skills": "Compétences",
    "nav.experience": "Expérience",
    "nav.portfolio": "Réalisations",
    "nav.fitScore": "Compatibilité",
    "nav.certifications": "Certifications",
    "nav.contact": "Contact",
    
    // Hero
    "hero.download": "Télécharger mon profil",
    "hero.contact": "Contact",
    "hero.role": "Stratégie & Communication",
    "hero.description": "J'accompagne les entreprises et les indépendants dans leur stratégie de marque, leur communication et leur développement commercial.",
    
    // Skills
    "skills.title": "Compétences",
    "skills.subtitle": "Expertise",
    "skills.strategy.title": "Stratégie",
    "skills.strategy.description": "Définition de positionnement, étude de marché et plans d'action sur-mesure.",
    "skills.communication.title": "Communication",
    "skills.communication.description": "Stratégies de communication multicanal pour maximiser votre visibilité.",
    "skills.branding.title": "Branding",
    "skills.branding.description": "Création et refonte d'identités de marque mémorables et cohérentes.",
    "skills.analytics.title": "Analyse & KPIs",
    "skills.analytics.description": "Suivi de performance et pilotage par la donnée pour des résultats mesurables.",
    "skills.growth.title": "Croissance",
    "skills.growth.description": "Stratégies d'acquisition et de fidélisation pour développer votre activité.",
    "skills.management.title": "Gestion de Projet",
    "skills.management.description": "Coordination d'équipes et pilotage de projets complexes, dans les délais.",
    
    // Experience
    "experience.title": "Parcours Professionnel",
    "experience.subtitle": "Parcours",
    "experience.current": "En cours",
    "experience.job1.title": "Consultante en Stratégie",
    "experience.job1.company": "Indépendante",
    "experience.job1.period": "2024 - Présent",
    "experience.job1.description": "Accompagnement stratégique pour PME et indépendants dans leur développement.",
    "experience.job1.mission1": "Audit de positionnement et recommandations stratégiques",
    "experience.job1.mission2": "Conception de plans de communication multicanal",
    "experience.job1.mission3": "Création de contenus et stratégie éditoriale",
    "experience.job1.mission4": "Suivi de performance et ajustements continus",
    "experience.job2.title": "Responsable Communication",
    "experience.job2.company": "Agence Créative",
    "experience.job2.period": "2021 - 2024",
    "experience.job2.description": "Pilotage de la communication pour un portefeuille de clients variés.",
    "experience.job2.mission1": "Gestion de campagnes de communication 360°",
    "experience.job2.mission2": "Relations presse et partenariats stratégiques",
    "experience.job2.mission3": "Management d'une équipe de 4 personnes",
    "experience.job2.mission4": "Reporting et analyse des résultats de campagne",
    
    // Certifications
    "certifications.title": "Certifications",
    "certifications.education": "Formation",
    "certifications.languages": "Langues",
    "certifications.inProgress": "En cours",
    "certifications.date1": "2024",
    "certifications.edu1.title": "Master Communication & Marketing",
    "certifications.edu1.subtitle": "Spécialité Stratégie Digitale",
    "certifications.edu1.level": "BAC +5",
    "certifications.edu2.title": "Licence Sciences de la Communication",
    "certifications.edu2.level": "BAC +3",
    "certifications.french": "Français",
    "certifications.french.level": "Natif",
    "certifications.english": "Anglais",
    "certifications.english.level": "Courant",
    
    // Contact
    "contact.title": "Me Contacter",
    "contact.subtitle": "Contact",
    "contact.description": "Vous avez un projet ou une collaboration en tête ? N'hésitez pas à me contacter.",
    "contact.name": "Nom complet",
    "contact.name.placeholder": "Votre nom",
    "contact.email": "Email",
    "contact.email.placeholder": "votre@email.com",
    "contact.subject": "Sujet",
    "contact.subject.placeholder": "Objet de votre message",
    "contact.message": "Message",
    "contact.message.placeholder": "Décrivez votre projet ou votre demande...",
    "contact.send": "Envoyer le message",
    "contact.sending": "Envoi en cours...",
    "contact.success.title": "Message envoyé avec succès !",
    "contact.success.description": "Je vous répondrai dans les plus brefs délais.",
    "contact.success.another": "Envoyer un autre message",
    "contact.error.name": "Le nom est requis",
    "contact.error.nameMax": "Le nom doit faire moins de 100 caractères",
    "contact.error.email": "Format d'email invalide",
    "contact.error.emailMax": "L'email doit faire moins de 255 caractères",
    "contact.error.subject": "Le sujet est requis",
    "contact.error.subjectMax": "Le sujet doit faire moins de 200 caractères",
    "contact.error.messageMin": "Le message doit faire au moins 10 caractères",
    "contact.error.messageMax": "Le message doit faire moins de 5000 caractères",
    
    // Portfolio
    "portfolio.title": "Projets Réalisés",
    "portfolio.subtitle": "Réalisations",
    "portfolio.project1.title": "Repositionnement de marque premium",
    "portfolio.project1.description": "Refonte complète du positionnement et de l'identité d'une marque de décoration haut de gamme.",
    "portfolio.project2.title": "Stratégie réseaux sociaux",
    "portfolio.project2.description": "Création et pilotage d'une stratégie social media ayant doublé l'engagement en 6 mois.",
    "portfolio.project3.title": "Lancement de produit",
    "portfolio.project3.description": "Stratégie de lancement complète pour une nouvelle gamme de cosmétiques naturels.",
    "portfolio.cta": "Intéressé(e) par une collaboration ?",
    "portfolio.contact": "Me contacter",
    "portfolio.viewProject": "Voir le projet",
    "portfolio.clickToView": "Cliquer pour voir",
    "portfolio.discussProject": "Discuter de ce projet",
    "portfolio.close": "Fermer",
    "portfolio.viewAll": "Voir toutes les réalisations",

    // Projects Page
    "projects.title": "Mes Réalisations",
    "projects.personalProject": "Projet Personnel",
    "projects.personalProjectPeriod": "2024 - Présent",
    "projects.visitSite": "Visiter le site",
    "projects.subtitle": "Portfolio Complet",
    "projects.description": "Découvrez l'ensemble de mes réalisations. Filtrez par domaine pour explorer les projets qui vous intéressent.",
    "projects.back": "Retour",
    "projects.filterBy": "Filtrer par",
    "projects.activeFilters": "Filtres actifs",
    "projects.clearAll": "Tout effacer",
    "projects.clearFilters": "Effacer les filtres",
    "projects.noResults": "Aucun projet trouvé",
    "projects.noResultsDescription": "Aucun projet ne correspond aux filtres sélectionnés.",
    "projects.technologies": "Domaines d'expertise",
    "projects.highlights": "Points clés",

    // Personal Projects Section
    "personalProjects.badge": "Projet Personnel",
    "personalProjects.title": "Mes Projets Personnels",
    "personalProjects.subtitle": "Découvrez les projets que je développe en dehors de mon activité professionnelle.",
    "personalProjects.featured": "Projet Phare",
    "personalProjects.visitSite": "Découvrir",
    "personalProjects.seeAllProjects": "Voir tous les projets",
    "personalProjects.azTrainer.title": "Le Guide du Personal Branding",
    "personalProjects.azTrainer.description": "Un guide complet et interactif pour aider les professionnels à construire leur marque personnelle et se démarquer dans leur secteur.",
    "personalProjects.azTrainer.highlight1": "Méthode en 5 étapes pour définir son positionnement unique",
    "personalProjects.azTrainer.highlight2": "Templates et exemples concrets pour chaque secteur",
    "personalProjects.azTrainer.highlight3": "Conseils pour développer sa présence en ligne",
    "personalProjects.azTrainer.highlight4": "Suivi de progression avec objectifs mesurables",

    // Why Hire Me
    "whyHireMe.title": "Pourquoi Me Choisir",
    "whyHireMe.subtitle": "Valeur Ajoutée",
    "whyHireMe.enterpriseFit": "Adéquation",

    // Fit Score
    "fitScore.title": "Score de Compatibilité",
    "fitScore.subtitle": "Compatibilité",

    // Currently Learning
    "currentlyLearning.title": "En cours d'apprentissage",
    "currentlyLearning.subtitle": "Veille & Formation",
    
    // Footer
    "footer.rights": "Tous droits réservés.",
  },
  en: {
    // Navigation
    "nav.skills": "Skills",
    "nav.experience": "Experience",
    "nav.portfolio": "Work",
    "nav.fitScore": "Compatibility",
    "nav.certifications": "Certifications",
    "nav.contact": "Contact",
    
    // Hero
    "hero.download": "Download Profile",
    "hero.contact": "Contact",
    "hero.role": "Strategy & Communication",
    "hero.description": "I help businesses and freelancers with their brand strategy, communication, and business development.",
    
    // Skills
    "skills.title": "Skills",
    "skills.subtitle": "Expertise",
    "skills.strategy.title": "Strategy",
    "skills.strategy.description": "Positioning definition, market research and tailored action plans.",
    "skills.communication.title": "Communication",
    "skills.communication.description": "Multichannel communication strategies to maximize your visibility.",
    "skills.branding.title": "Branding",
    "skills.branding.description": "Creating and revamping memorable and consistent brand identities.",
    "skills.analytics.title": "Analytics & KPIs",
    "skills.analytics.description": "Performance tracking and data-driven management for measurable results.",
    "skills.growth.title": "Growth",
    "skills.growth.description": "Acquisition and retention strategies to grow your business.",
    "skills.management.title": "Project Management",
    "skills.management.description": "Team coordination and complex project management, on schedule.",
    
    // Experience
    "experience.title": "Professional Experience",
    "experience.subtitle": "Career",
    "experience.current": "Current",
    "experience.job1.title": "Strategy Consultant",
    "experience.job1.company": "Freelance",
    "experience.job1.period": "2024 - Present",
    "experience.job1.description": "Strategic guidance for SMBs and freelancers in their growth journey.",
    "experience.job1.mission1": "Positioning audit and strategic recommendations",
    "experience.job1.mission2": "Multichannel communication plan design",
    "experience.job1.mission3": "Content creation and editorial strategy",
    "experience.job1.mission4": "Performance tracking and continuous adjustments",
    "experience.job2.title": "Communication Manager",
    "experience.job2.company": "Creative Agency",
    "experience.job2.period": "2021 - 2024",
    "experience.job2.description": "Managing communications for a diverse client portfolio.",
    "experience.job2.mission1": "360° communication campaign management",
    "experience.job2.mission2": "Press relations and strategic partnerships",
    "experience.job2.mission3": "Managing a team of 4 people",
    "experience.job2.mission4": "Campaign results reporting and analysis",
    
    // Certifications
    "certifications.title": "Certifications",
    "certifications.education": "Education",
    "certifications.languages": "Languages",
    "certifications.inProgress": "In progress",
    "certifications.date1": "2024",
    "certifications.edu1.title": "Master's in Communication & Marketing",
    "certifications.edu1.subtitle": "Digital Strategy Specialization",
    "certifications.edu1.level": "Master's level",
    "certifications.edu2.title": "Bachelor's in Communication Sciences",
    "certifications.edu2.level": "Bachelor's level",
    "certifications.french": "French",
    "certifications.french.level": "Native",
    "certifications.english": "English",
    "certifications.english.level": "Fluent",
    
    // Contact
    "contact.title": "Contact Me",
    "contact.subtitle": "Contact",
    "contact.description": "Have a project or collaboration in mind? Don't hesitate to get in touch.",
    "contact.name": "Full name",
    "contact.name.placeholder": "Your name",
    "contact.email": "Email",
    "contact.email.placeholder": "your@email.com",
    "contact.subject": "Subject",
    "contact.subject.placeholder": "Subject of your message",
    "contact.message": "Message",
    "contact.message.placeholder": "Describe your project or request...",
    "contact.send": "Send message",
    "contact.sending": "Sending...",
    "contact.success.title": "Message sent successfully!",
    "contact.success.description": "I will respond as soon as possible.",
    "contact.success.another": "Send another message",
    "contact.error.name": "Name is required",
    "contact.error.nameMax": "Name must be less than 100 characters",
    "contact.error.email": "Invalid email format",
    "contact.error.emailMax": "Email must be less than 255 characters",
    "contact.error.subject": "Subject is required",
    "contact.error.subjectMax": "Subject must be less than 200 characters",
    "contact.error.messageMin": "Message must be at least 10 characters",
    "contact.error.messageMax": "Message must be less than 5000 characters",
    
    // Portfolio
    "portfolio.title": "Projects",
    "portfolio.subtitle": "Work",
    "portfolio.project1.title": "Premium Brand Repositioning",
    "portfolio.project1.description": "Complete repositioning and identity overhaul for a high-end home décor brand.",
    "portfolio.project2.title": "Social Media Strategy",
    "portfolio.project2.description": "Creation and management of a social media strategy that doubled engagement in 6 months.",
    "portfolio.project3.title": "Product Launch",
    "portfolio.project3.description": "Complete launch strategy for a new natural cosmetics range.",
    "portfolio.cta": "Interested in working together?",
    "portfolio.contact": "Contact me",
    "portfolio.viewProject": "View project",
    "portfolio.clickToView": "Click to view",
    "portfolio.discussProject": "Discuss this project",
    "portfolio.close": "Close",
    "portfolio.viewAll": "View all projects",

    // Projects Page
    "projects.title": "My Projects",
    "projects.personalProject": "Personal Project",
    "projects.personalProjectPeriod": "2024 - Present",
    "projects.visitSite": "Visit site",
    "projects.subtitle": "Full Portfolio",
    "projects.description": "Explore all my achievements. Filter by area to find projects that interest you.",
    "projects.back": "Back",
    "projects.filterBy": "Filter by",
    "projects.activeFilters": "Active filters",
    "projects.clearAll": "Clear all",
    "projects.clearFilters": "Clear filters",
    "projects.noResults": "No projects found",
    "projects.noResultsDescription": "No projects match the selected filters.",
    "projects.technologies": "Areas of expertise",
    "projects.highlights": "Key highlights",

    // Personal Projects Section
    "personalProjects.badge": "Personal Project",
    "personalProjects.title": "My Personal Projects",
    "personalProjects.subtitle": "Discover the projects I build outside my professional work.",
    "personalProjects.featured": "Featured Project",
    "personalProjects.visitSite": "Discover",
    "personalProjects.seeAllProjects": "See all projects",
    "personalProjects.azTrainer.title": "The Personal Branding Guide",
    "personalProjects.azTrainer.description": "A comprehensive, interactive guide to help professionals build their personal brand and stand out in their industry.",
    "personalProjects.azTrainer.highlight1": "5-step method to define your unique positioning",
    "personalProjects.azTrainer.highlight2": "Templates and concrete examples for each industry",
    "personalProjects.azTrainer.highlight3": "Tips for developing your online presence",
    "personalProjects.azTrainer.highlight4": "Progress tracking with measurable goals",

    // Why Hire Me
    "whyHireMe.title": "Why Choose Me",
    "whyHireMe.subtitle": "Value Proposition",
    "whyHireMe.enterpriseFit": "Fit",

    // Fit Score
    "fitScore.title": "Compatibility Score",
    "fitScore.subtitle": "Fit Score",

    // Currently Learning
    "currentlyLearning.title": "Currently Learning",
    "currentlyLearning.subtitle": "Continuous Learning",
    
    // Footer
    "footer.rights": "All rights reserved.",
  },
};

export const LanguageProvider = ({ children }: { children: ReactNode }) => {
  const [language, setLanguage] = useState<Language>(() => {
    if (typeof window !== "undefined") {
      const saved = localStorage.getItem("language") as Language;
      if (saved) return saved;
      const browserLang = navigator.language.split("-")[0];
      return browserLang === "fr" ? "fr" : "en";
    }
    return theme.defaultLanguage;
  });

  useEffect(() => {
    localStorage.setItem("language", language);
  }, [language]);

  const t = (key: string): string => {
    return translations[language][key] || key;
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error("useLanguage must be used within a LanguageProvider");
  }
  return context;
};
